import {
  useGetAllCourses,
  useGetAllMajorNames,
  useGetUniversitiesLight,
  useSearchMentors,
  useUpdateEnrollment,
} from '@actions';
import {
  CheckCircleOutlined,
  ExclamationCircleFilled,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import MentorsTableHeader from '@components/V3/MentorsTableHeader';
import MentorsTableRow from '@components/V3/MentorsTableRow';
import AgoraSpinner from '@components/V3/Utils/AgoraSpinner';
import Select, { SelectOption } from '@components/V4/Select/Select';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDebounce } from '@hooks';
import { Enrollment } from '@shared/common';
import { Pagination } from 'antd';
import { useGetAllMentors } from 'apps/agora/src/actions/users.actions';
import useToast from 'apps/agora/src/hooks/useToast';
import { COLORS } from 'apps/agora/src/v3/global';
import { useEffect, useState } from 'react';
import React from 'react';
import { useController, useForm } from 'react-hook-form';
import { z } from 'zod';
import DetailsModal, { DetailsModalPage } from '../../DetailsModal';
import SessionModalFormWrapper from '../../DetailsModalFormWrapper';
import SuggestedMentorsPanel from '../../SuggestedMentorsPanel';

interface ChangeMentorFormProps {
  defaultData: Enrollment;
  onCloseEditMode: () => void;
}

const formDataSchema = z.object({
  mentor: z.string().min(1, { message: 'Please select a mentor.' }),
});

type FormData = z.infer<typeof formDataSchema>;

const ChangeMentorForm = (props: ChangeMentorFormProps) => {
  const { defaultData, onCloseEditMode } = props;

  const toast = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(4);

  const [mentorId, setMentorId] = useState<string>();
  const [modalPage, setModalPage] = useState<DetailsModalPage>('mentor');
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [mentorSearch, setMentorSearch] = useState('');
  const [suggestedMentor, setSuggestedMentor] = useState<{ _id: string; fullName: string }>();
  const [universitySearch, setUniversitySearch] = useState('');
  const [universities, setUniversities] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>(['Active', 'Available']);
  const [majors, setMajors] = useState<string[]>([]);
  const [course, setCourse] = useState<string[]>([defaultData.course._id]);

  const debouncedMentorSearch = useDebounce(mentorSearch, 500);
  const debouncedUniversitySearch = useDebounce(universitySearch, 500);

  const { data: mentorsData, isLoading: mentorsLoading } = useGetAllMentors({
    search: debouncedMentorSearch,
    active: true,
  });

  const { data: mentorSuggestions, isLoading: isLoadingMentors } = useSearchMentors({
    page: currentPage,
    limit: pageSize,
    sorting: {},
    statuses: statuses.length > 0 ? statuses : undefined,
    isTestUser: false,
    isDeactivated: false,
    courses: course,
    majors: majors.length > 0 ? majors : undefined,
    universities: universities.length > 0 ? universities : undefined,
  });

  const { data: allCourses } = useGetAllCourses();
  const { data: allMajors } = useGetAllMajorNames();
  const { data: allUniversities, isLoading: isUniversitiesLoading } = useGetUniversitiesLight({
    search: debouncedUniversitySearch,
    limit: 20,
  });

  let mentorsOptions: { label: string; value: string }[] = [];
  if (mentorsData) {
    mentorsOptions = mentorsData.map((user) => ({
      label: user.fullName,
      value: user._id,
    }));
  }
  if (suggestedMentor && !mentorsOptions.some((option) => option.value === suggestedMentor._id)) {
    mentorsOptions.unshift({ label: suggestedMentor.fullName, value: suggestedMentor._id });
  }
  mentorsOptions.unshift({ label: 'No Mentor', value: 'TBD' });

  const mentorStatuses = [
    { label: 'In Training', value: 'In Training' },
    { label: 'Active', value: 'Active' },
    { label: 'Available', value: 'Available' },
    { label: 'Under Observation', value: 'Under Observation' },
    { label: 'At Capacity', value: 'At Capacity' },
    { label: 'Leaving', value: 'Leaving' },
    { label: 'Inactive', value: 'Inactive' },
  ];

  const getUniversityOptions = (): SelectOption[] =>
    allUniversities?.map((u: any) => ({ label: u.name, value: u._id })) || [];

  const getMajorOptions = (): SelectOption[] =>
    allMajors?.map((m: any) => ({ value: m.name, label: m.name })) || [];

  const allOption = { label: 'ALL', value: null };

  const getCoursesOptions = () => {
    const courseOptions: SelectOption[] =
      allCourses?.map((course: any) => ({
        label: course.title,
        value: course._id,
      })) || [];

    if (!debouncedMentorSearch) {
      courseOptions.unshift(allOption);
    }

    return courseOptions;
  };

  const { handleSubmit, trigger, setValue, control, getValues } = useForm<FormData>({
    resolver: zodResolver(formDataSchema),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
  });

  const { field: mentorField, fieldState: mentorFieldState } = useController({
    name: 'mentor',
    control,
  });

  const {
    mutate: updateEnrollment,
    data,
    isLoading,
  } = useUpdateEnrollment({
    onSuccess: async () => {
      toast.success('Successfully updated the mentor.');
    },
    onError: () => {
      toast.error('Mentor could not be updated.');
    },
  });

  useEffect(() => {
    if (data) {
      if (data.mailSent === undefined || data.mailSent) {
        const timeout = setTimeout(() => {
          onCloseEditMode();
        }, 2000);
        return () => clearTimeout(timeout);
      }
    }
    return;
  }, [data, onCloseEditMode]);

  const onSubmit = async (formData: FormData) => {
    const isValid = await trigger();
    if (isValid) {
      updateEnrollment({
        enrollmentId: defaultData._id,
        mentorId: formData.mentor,
      });
    }
  };

  const submitHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    handleSubmit(onSubmit)(e);
  };

  const mentorSelectorBlurHandler = () => {
    mentorField.onBlur();
    setMentorSearch('');
  };

  const showMentorPageModalHandler = (mentorId: string) => {
    setMentorId(mentorId);
    setShowDetailsModal(true);
    setModalPage('mentor');
  };

  const handleCloseModal = () => {
    setMentorId(undefined);
    setShowDetailsModal(false);
    setModalPage(undefined);
  };

  const handleClearMajors = () => {
    setMajors([]);
    setCurrentPage(1);
  };

  const handleAddMajor = (major: string) => {
    setMajors((prev) => [...prev, major]);
    setCurrentPage(1);
  };

  const handleRemoveMajor = (major: string) => {
    setMajors((prev) => prev.filter((m) => m !== major));
    setCurrentPage(1);
  };

  const handleClearUniversities = () => {
    setUniversities([]);
    setCurrentPage(1);
  };

  const handleAddUniversity = (universityId: string) => {
    if (!universities.includes(universityId)) {
      setUniversities((prev) => [...prev, universityId]);
      setCurrentPage(1);
    }
  };

  const handleRemoveUniversity = (universityId: string) => {
    setUniversities((prev) => prev.filter((id) => id !== universityId));
    setCurrentPage(1);
  };

  const handleAddStatus = (status: string) => {
    setStatuses((prev) => [...prev, status]);
    setCurrentPage(1);
  };

  const handleRemoveStatus = (status: string) => {
    setStatuses((prev) => prev.filter((s) => s !== status));
    setCurrentPage(1);
  };

  const handleClearStatuses = () => {
    setStatuses([]);
    setCurrentPage(1);
  };

  const handleCourse = (course: any) => {
    setCourse([course]);
    setCurrentPage(1);
  };

  const handleClearCourse = () => {
    setCourse([]);
    setCurrentPage(1);
  };

  const MailStatusIndicator = () => {
    if (data?.mailSent === undefined) return null;
    return (
      <div className="flex items-center gap-2 mt-2">
        {data.mailSent ? (
          <>
            <CheckCircleOutlined style={{ width: '20px', color: COLORS.PRIMARY }} />
            <span className="text-green-600 text-sm">Email successfully sent</span>
          </>
        ) : (
          <>
            <ExclamationCircleFilled style={{ width: '20px', color: COLORS.ERROR }} />
            <span className="text-red-600 text-sm">Email could not be sent</span>
          </>
        )}
      </div>
    );
  };

  return (
    <SessionModalFormWrapper
      title="Mentor"
      isLoading={isLoading}
      onCancel={onCloseEditMode}
      submitHandler={submitHandler}
    >
      <Select
        label="Mentor"
        value={mentorField.value}
        filterOption={() => true}
        searchValue={mentorSearch}
        onSearch={setMentorSearch}
        onSelect={(value) => mentorField.onChange(value)}
        onBlur={mentorSelectorBlurHandler}
        options={mentorsOptions}
        notFoundContent={
          mentorsLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
        }
        placeholder="Search Mentor"
        variant="primary"
        isLoading={mentorsLoading}
        isValid={!mentorFieldState.error}
        isTouched={mentorFieldState.isTouched}
        errorText={mentorFieldState.error?.message}
        isRequired
      />

      <MailStatusIndicator />

      <SuggestedMentorsPanel
        className="my-4"
        courseId={defaultData.course?._id}
        studentId={defaultData.user?._id}
        enrollmentId={defaultData._id}
        selectedMentorId={mentorField.value}
        onSelect={(mentor) => {
          setSuggestedMentor(mentor);
          mentorField.onChange(mentor._id);
        }}
      />

      <div style={{ backgroundColor: COLORS.SECTION_BACKGROUND, color: COLORS.TEXT_PRIMARY }}>
        <div
          style={{
            backgroundColor: COLORS.DARK_GREY_ALT,
            padding: '10px',
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Select
            className="max-w-64"
            variant="secondary"
            label="Statuses"
            value={statuses}
            onSelect={(v) => handleAddStatus(v as string)}
            onDeselect={(v) => handleRemoveStatus(v as string)}
            onClear={handleClearStatuses}
            options={mentorStatuses}
            mode="multiple"
          />

          <Select
            className="max-w-64"
            variant="secondary"
            label="Majors"
            mode="multiple"
            value={majors}
            onSelect={(v) => handleAddMajor(v as string)}
            onDeselect={(v) => handleRemoveMajor(v as string)}
            onClear={handleClearMajors}
            options={getMajorOptions()}
          />

          <Select
            className="max-w-64"
            label="University"
            value={universities}
            onSearch={setUniversitySearch}
            searchValue={universitySearch}
            onSelect={(value) => {
              handleAddUniversity(value as string);
              setUniversitySearch('');
            }}
            onDeselect={(value) => handleRemoveUniversity(value as string)}
            onClear={handleClearUniversities}
            options={getUniversityOptions()}
            notFoundContent={
              isUniversitiesLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
            }
            placeholder="Search Universities"
            variant="secondary"
            mode="multiple"
          />

          <Select
            className="max-w-64"
            variant="secondary"
            label="Course"
            value={course}
            onSelect={handleCourse}
            options={getCoursesOptions()}
            onClear={handleClearCourse}
          />
        </div>
        <div className="overflow-auto">
          <table className="mentors-table">
            <MentorsTableHeader>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Status</th>
                <th>Capacity</th>
                <th>Max Cap.</th>
                <th>Cap. Last Update</th>
                <th>Pay Rate</th>
                <th>Universities</th>
                <th>Courses</th>
                <th>Majors</th>
                <th>Degrees</th>
                <th>Created At</th>
                <th>Access</th>
              </tr>
            </MentorsTableHeader>
            <tbody>
              {!isLoadingMentors ? (
                mentorSuggestions?.mentors?.map((item) => (
                  <MentorsTableRow
                    key={item?.mentor?._id}
                    mentorData={item}
                    refetchMentorStatistics={() => {}}
                    refetchSearchMentors={() => {}}
                    onProfileModalOpen={() => showMentorPageModalHandler(item?.mentor?._id)}
                  />
                ))
              ) : (
                <AgoraSpinner fontSize={32} margin="32px" />
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            padding: '10px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ color: COLORS.TEXT_SECONDARY }}>
            {`${(currentPage - 1) * pageSize + 1}-${Math.min(
              currentPage * pageSize,
              mentorSuggestions?.total || 0
            )} of ${mentorSuggestions?.total || 0}`}
          </div>
          <Pagination
            className="custom-pagination"
            showSizeChanger={false}
            current={currentPage}
            total={mentorSuggestions?.total}
            pageSize={pageSize}
            onChange={(page) => setCurrentPage(page)}
            prevIcon={<LeftOutlined style={{ color: COLORS.TEXT_SECONDARY }} />}
            nextIcon={<RightOutlined style={{ color: COLORS.TEXT_SECONDARY }} />}
          />
        </div>
        {showDetailsModal && (
          <DetailsModal
            mentorId={mentorId}
            page={modalPage}
            isOpen={true}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </SessionModalFormWrapper>
  );
};

export default ChangeMentorForm;
